/**
 * internalAccess — the single internal-staff allowlist (task #56).
 *
 * Promoted out of useRealtimeDesktopTarget so every gate that must NEVER
 * trap an internal account (workspace lock, desktop target resolution) reads
 * the same list. Internal accounts have no users/{uid}/subscription doc and
 * often no org back-pointer, so any "no billing → onboarding" logic has to
 * consult this before locking anyone in.
 */

export const INTERNAL_EMAILS = new Set([
  'justin@prest0.ai',
  'miles@prest0.ai',
  'miles.projectolivia@gmail.com', // Miles' primary sign-in (2026-07-31 /desktop fix)
  'prest0n@prest0.ai',
]);

// Known Firebase UIDs for internal users (covers cases where the auth user
// has no `email` field, e.g. anonymous-link or provider-less sessions).
export const INTERNAL_UIDS = new Set([
  'rbqXyAO1iFS01ArcEMMFmtUQv1y1', // Justin
  'oUZYZfY7PCPYOQm2b5UqQ4NkU1u1', // Prest0n
]);

export function isInternalAccount(opts: { uid?: string | null; email?: string | null }): boolean {
  if (opts.email && INTERNAL_EMAILS.has(opts.email.toLowerCase())) return true;
  if (opts.uid && INTERNAL_UIDS.has(opts.uid)) return true;
  return false;
}

/**
 * The platform's own agent identity — Prest0n's Firebase uid and the
 * e-mail its member docs carry. It IS the assistant, never a person: it holds
 * a real member doc in prest0-inc (`memberType: 'service-account'`, displayName
 * "Prest0n (agent)"), which is exactly how "add Prest0n" appeared as an
 * addable HUMAN in the add-people picker on 2026-09-08. Every people roster
 * (the picker, the composer, the @-mention resolver) drops it through
 * `isPlatformAgentIdentity`; the assistant's own row is rendered on purpose,
 * from the registry's `assistantEnabled` fact, never from a member doc.
 */
export const PLATFORM_AGENT_UID = 'oUZYZfY7PCPYOQm2b5UqQ4NkU1u1';
export const PLATFORM_AGENT_EMAILS = new Set(['prest0n@prest0.ai']);

export function isPlatformAgentIdentity(opts: {
  uid?: string | null;
  email?: string | null;
  memberType?: string | null;
}): boolean {
  if ((opts.memberType ?? '').trim().toLowerCase() === 'service-account') return true;
  if (opts.uid && opts.uid.trim() === PLATFORM_AGENT_UID) return true;
  if (opts.email && PLATFORM_AGENT_EMAILS.has(opts.email.trim().toLowerCase())) return true;
  return false;
}

/**
 * The platform's own organization account id (Prest0 Inc.). Mirrors the
 * server pin `PREST0_INC_ACCOUNT_ID` in functions/src/rbac/developerOrgGate.ts
 * — a stable platform fact, not per-deploy config. Used CLIENT-SIDE only to
 * decide whether a platform-owner-only affordance (the cross-org activity
 * lookup) may render in the CURRENT workspace; the server independently
 * re-checks membership on every call, so this is cosmetic gating, never the
 * authority. If the pin ever moves it must change in both places.
 */
export const PREST0_INC_ACCOUNT_ID = 'prest0-inc';
